import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdTreadAgentConversations } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import { TreadAgentConversationListing } from '~hooks/useTreadAgent/models';
import { TREAD_AGENT_QUERY_KEY } from '~queries/treadAgentQueryKeys';
import connection from '~services/connectionModule';
import { Nullable } from '~types/Nullable';

/**
 * The company's Tread Agent conversations; ordering is left to the caller.
 * Server state only — the live streaming turn lives in TreadAgentStore, never here.
 *
 * `enabled` lets the caller defer the fetch until the list is actually needed (e.g. the
 * dock only opens its history popover on demand), so we don't pull every user's history on
 * app load for a feature they may never open. React Query keeps the cache while disabled, so
 * reopening shows the last result instantly and refreshes in the background.
 */
export default function useTreadAgentConversationsQuery(
  companyId: Nullable<string>,
  options?: { enabled?: boolean },
) {
  const query = useQuery({
    queryKey: [TREAD_AGENT_QUERY_KEY, 'conversations', companyId],
    queryFn: companyId
      ? async ({ signal }) => {
          try {
            const response = await getV1CompaniesCompanyIdTreadAgentConversations({
              path: { 'company-id': companyId },
              signal,
            });
            return response.data.data.map(TreadAgentConversationListing.parse);
          } catch (error) {
            connection.handleRequestError(
              error,
              t('error_messages.tread_agent.failed_to_fetch_chats'),
            );
            throw error;
          }
        }
      : skipToken,
    enabled: options?.enabled ?? true,
  });

  const conversations = useMemo(() => query.data ?? [], [query.data]);

  return { conversations, isFetching: query.isFetching };
}
